import { motion } from "framer-motion";
import { useEffect } from "react";
import Contact from "../components/Contact";
import { useTheme } from "../context/ThemeContext";
import Logo from "../components/Logo";
import MetaData from "../components/MetaData";

const ToS = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.style.scrollBehavior = "auto";
    document.body.style.scrollBehavior = "auto";
    return () => {
      document.documentElement.style.scrollBehavior = "smooth";
      document.body.style.scrollBehavior = "smooth";
    };
  }, []);

  const { darkMode } = useTheme();

  const tosStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Terms of Service - Nova Creations",
    description:
      "Terms of Service for Nova Creations Minecraft building services including payment, refund policy, and copyright information.",
    publisher: {
      "@type": "Organization",
      name: "Nova Creations",
    },
    dateModified: new Date().toISOString(),
    inLanguage: "en-US",
  };

  return (
    <div className="min-h-screen text-white overflow-auto">
      <MetaData
        title="Terms of Service - Nova Creations | Minecraft Building Services"
        description="Read Nova Creations' Terms of Service for Minecraft building services. Includes payment terms, refund policy, copyright information, and service guidelines."
        keywords="nova creations terms of service, minecraft building terms, service agreement, refund policy, minecraft commission terms"
        url="https://www.novacreations.site/terms"
        image="https://i.imgur.com/qsv10H9.png"
        structuredData={tosStructuredData}
        type="article"
      />
      <div className="absolute inset-0 -z-5">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: darkMode ? 1 : 0 }}
          transition={{ duration: 0.7, ease: "easeInOut" }}
          className="fixed inset-0 bg-[linear-gradient(to_right,#2d3c61_1px,transparent_1px),linear-gradient(to_bottom,#2d3c61_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:radial-gradient(ellipse_70%_100%_at_58%_0%,#4d4dff_0%,transparent_110%)]"
        />
      </div>

      <Logo />
      <motion.div
        className="container mx-auto px-4 pt-20 pb-16 md:pt-32"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl md:text-4xl font-minecraft mb-6 md:mb-8 drop-shadow-2xl text-center md:text-left">
          Terms of Service
        </h1>
        <h2 className="text-xl md:text-2xl font-minecraft mb-2 md:mb-4">
          {" "}
          Last update date: 08/10/2025
        </h2>
        <div className="bg-slate-600 bg-opacity-50 backdrop-blur-lg text-white p-4 rounded-lg shadow-lg w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 p-2 md:p-8 prose-invert">
            <section className="bg-slate-600 bg-opacity-50 backdrop-blur-lg text-white p-4 md:p-6 w-auto h-auto rounded-lg shadow-lg transition-all duration-1000 ease-in-out hover:shadow-cyan-600">
              <h2 className="text-xl md:text-2xl font-minecraft mb-2 md:mb-4">
                1. General Provisions
              </h2>
              <p className="text-sm md:text-base">
                This document outlines the terms and conditions for the
                provision of project building services in the game Minecraft
                (hereinafter referred to as "Services") by Nova Creations
                (hereinafter referred to as the "Service Provider").
              </p>
              <p className="mt-2 md:mt-4 text-sm md:text-base">
                The Client (hereinafter referred to as "Client") may be a
                natural or legal person who orders Services in accordance with
                these terms and conditions.
              </p>
              <p className="mt-2 md:mt-4 text-sm md:text-base">
                By using the Services, the Client accepts all the terms set
                forth in this Terms of Service.
              </p>
            </section>

            <section className="bg-slate-600 bg-opacity-50 backdrop-blur-lg text-white p-4 md:p-6 w-auto h-auto rounded-lg shadow-lg transition-all duration-1000 ease-in-out hover:shadow-cyan-600">
              <h2 className="text-xl md:text-2xl font-minecraft mb-2 md:mb-4">
                2. Scope of Services
              </h2>
              <p className="text-sm md:text-base">
                The Service Provider offers the realization of projects in the
                game Minecraft, such as buildings, maps, or other elements
                according to the specifications provided by the Client.
              </p>
              <p className="mt-2 md:mt-4 text-sm md:text-base">
                Each project is tailored to the specifications given by the
                Client when placing the order.
              </p>
              <p className="mt-2 md:mt-4 text-sm md:text-base">
                The Service Provider reserves the right to refuse the execution
                of projects that violate copyright laws, legal regulations, or
                contain content deemed inappropriate.
              </p>
            </section>

            <section className="bg-slate-600 bg-opacity-50 backdrop-blur-lg text-white p-4 md:p-6 w-auto h-auto rounded-lg shadow-lg transition-all duration-1000 ease-in-out hover:shadow-cyan-600">
              <h2 className="text-xl md:text-2xl font-minecraft mb-2 md:mb-4">
                3. Order and Payment Process
              </h2>
              <p className="mb-2 md:mb-4 text-sm md:text-base">
                The Client places the order via Discord.
              </p>
              <p className="mb-2 md:mb-4 text-sm md:text-base">
                Upon receiving the project details, the Service Provider sends a
                price estimate and an approximate completion time.
              </p>
              <p className="mb-2 md:mb-4 text-sm md:text-base">
                Work begins after the terms are accepted and a deposit of at
                least 50% is made.
              </p>
              <p className="mb-2 md:mb-4 text-sm md:text-base">
                The remaining amount is due upon completion of the project and
                delivery of the final work to the Client.
              </p>
              <p className="text-sm md:text-base">
                Payments are made via PayPal or another payment method agreed
                upon by the Service Provider and the Client.
              </p>
            </section>

            <section className="bg-slate-600 bg-opacity-50 backdrop-blur-lg text-white p-4 md:p-6 w-auto h-auto rounded-lg shadow-lg transition-all duration-1000 ease-in-out hover:shadow-cyan-600">
              <h2 className="text-xl md:text-2xl font-minecraft mb-2 md:mb-4">
                4. Project Execution
              </h2>
              <p className="text-sm md:text-base">
                The Service Provider will make every effort to ensure the
                project is completed according to the agreed specifications and
                within the set deadline.
              </p>
              <ul className="list-disc list-inside mt-2 md:mt-4 space-y-1 md:space-y-2 text-sm md:text-base">
                <li>
                  Significant changes to the project may require renegotiation
                  of the price and completion time.
                </li>
                <li>
                  Significant changes to the project may require renegotiation
                  of the price and completion time.
                </li>
                <li>
                  The final version of the project will be delivered to the
                  Client in the format agreed upon at the time of the order
                  (e.g., map file, schematic).
                </li>
              </ul>
            </section>

            <section className="bg-slate-600 bg-opacity-50 backdrop-blur-lg text-white p-4 md:p-6 w-auto h-auto rounded-lg shadow-lg transition-all duration-1000 ease-in-out hover:shadow-cyan-600">
              <h2 className="text-xl md:text-2xl font-minecraft mb-2 md:mb-4">
                5. Copyright and Licenses
              </h2>
              <ul className="list-disc list-inside space-y-1 md:space-y-2 text-sm md:text-base">
                <li>
                  The Service Provider retains the copyright to all projects
                  until full payment is received.
                </li>
                <li>
                  Once full payment is made, the Client receives the rights to
                  use the project for the purposes specified during the order.
                </li>
                <li>
                  The Client is not entitled to resell the project or claim
                  authorship unless otherwise agreed.
                </li>
              </ul>
            </section>

            <section className="bg-slate-600 bg-opacity-50 backdrop-blur-lg text-white p-4 md:p-6 w-auto h-auto rounded-lg shadow-lg transition-all duration-1000 ease-in-out hover:shadow-cyan-600">
              <h2 className="text-xl md:text-2xl font-minecraft mb-2 md:mb-4">
                6. Liability and Complaints
              </h2>
              <ul className="list-disc list-inside space-y-1 md:space-y-2 text-sm md:text-base">
                <li>
                  The Service Provider is not liable for any damage resulting
                  from improper use of the project.
                </li>
                <li>
                  Complaints about quality must be made during project creation.
                </li>
                <li>
                  No complaints will be accepted after delivery, as these are
                  digital goods.
                </li>
              </ul>
            </section>

            <section className="bg-slate-600 bg-opacity-50 backdrop-blur-lg text-white p-4 md:p-6 w-auto h-auto rounded-lg shadow-lg transition-all duration-1000 ease-in-out hover:shadow-cyan-600">
              <h2 className="text-xl md:text-2xl font-minecraft mb-2 md:mb-4">
                7. Privacy Policy
              </h2>
              <p className="text-sm md:text-base">
                The Service Provider processes the Client's personal data solely
                for the purpose of fulfilling the order and in compliance with
                applicable data protection laws.
              </p>
            </section>

            <section className="bg-slate-600 bg-opacity-50 backdrop-blur-lg text-white p-4 md:p-6 w-auto h-auto rounded-lg shadow-lg transition-all duration-1000 ease-in-out hover:shadow-cyan-600">
              <h2 className="text-xl md:text-2xl font-minecraft mb-2 md:mb-4">
                7.1 Nature of the Services
              </h2>
              <p className="text-sm md:text-base">
                All projects created by the Service Provider are custom digital
                services, made specifically to the Client's individual
                requirements. Because of this, once work has started, refunds
                are generally not available, as the project represents
                personalized digital content.
              </p>
            </section>

            <section className="bg-slate-600 bg-opacity-50 backdrop-blur-lg text-white p-4 md:p-6 w-auto h-auto rounded-lg shadow-lg transition-all duration-1000 ease-in-out hover:shadow-cyan-600">
              <h2 className="text-xl md:text-2xl font-minecraft mb-2 md:mb-4">
                7.2 Refunds Before Work Begins
              </h2>
              <p className="text-sm md:text-base">
                A 100% refund is available if the Client cancels the order
                before work has started.
              </p>
            </section>

            <section className="bg-slate-600 bg-opacity-50 backdrop-blur-lg text-white p-4 md:p-6 w-auto h-auto rounded-lg shadow-lg transition-all duration-1000 ease-in-out hover:shadow-cyan-600">
              <h2 className="text-xl md:text-2xl font-minecraft mb-2 md:mb-4">
                7.3 Refunds During Project Execution
              </h2>
              <p className="text-sm md:text-base">
                If the Client cancels the order after work has begun but before
                completion, a partial refund may be granted, based on the
                progress of the project:
              </p>
              <ul className="list-disc list-inside mt-2 md:mt-4 space-y-1 md:space-y-2 text-sm md:text-base">
                <li>
                  Up to 25% completion — refund of 75% of the amount paid.
                </li>
                <li>25–50% completion — refund of 50% of the amount paid.</li>
                <li>Over 50% completion — no refund.</li>
              </ul>
              <p className="mt-2 md:mt-4 text-sm md:text-base">
                The Service Provider determines the refund amount based on the
                actual stage of completion.
              </p>
            </section>

            <section className="bg-slate-600 bg-opacity-50 backdrop-blur-lg text-white p-4 md:p-6 w-auto h-auto rounded-lg shadow-lg transition-all duration-1000 ease-in-out hover:shadow-cyan-600">
              <h2 className="text-xl md:text-2xl font-minecraft mb-2 md:mb-4">
                7.4 Refunds After Delivery
              </h2>
              <p className="text-sm md:text-base">
                Once the final version of the project has been delivered, no
                refunds will be issued, as the work constitutes custom digital
                content made to order.
              </p>
            </section>

            <section className=" bg-slate-600 bg-opacity-50 backdrop-blur-lg text-white p-4 md:p-6 w-auto h-auto rounded-lg shadow-lg transition-all duration-1000 ease-in-out hover:shadow-cyan-600">
              <h2 className="text-xl md:text-2xl font-minecraft mb-2 md:mb-4">
                7.5 Complaints and Revisions
              </h2>
              <p className="text-sm md:text-base">
                Any complaints or requests for revisions must be made during the
                creation process. After delivery and acceptance of the final
                product, no further complaints will be accepted.
              </p>
            </section>
            <section className="col-span-full bg-slate-600 bg-opacity-50 backdrop-blur-lg text-white p-4 md:p-6 w-auto h-auto rounded-lg shadow-lg transition-all duration-1000 ease-in-out hover:shadow-cyan-600">
              <h2 className="text-xl md:text-2xl font-minecraft mb-2 md:mb-4">
                8. Final Provisions
              </h2>
              <ul className="list-disc list-inside space-y-1 md:space-y-2 text-sm md:text-base">
                <li>
                  The Service Provider reserves the right to amend this Terms of
                  Service without prior notice to the Client. However, for
                  ongoing orders, the terms that were in effect at the start of
                  the work shall apply.
                </li>
                <li>
                  In matters not regulated by these terms, the relevant
                  provisions of law will apply.
                </li>
                <li>
                  Any disputes arising from the provision of Services will be
                  resolved by the court with jurisdiction over the location of
                  the Service Provider's headquarters.
                </li>
              </ul>
            </section>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ToS;
